import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Row, Col } from 'reactstrap';
import Body from '../Components/layout/Body';
import { TestDriveInfoTable } from '../Components/layout/DisplayTestDriveInfo';
import { TABS } from './ConstPaths';

const REVIEW_CHOICES_HELP = `
  <div>
    <p><strong>Purpose: </strong>Here you can review Your Prospects to see if you want to move some into Your Picks. 
    </p>
    <p>The criteria you’ll use for this is up to you, but here are some guidelines.  
    </p>
    <p>If an access method shows promise, you may want to Add to Picks.  
    You would definitely do this for something that you are going to recommend for the client, 
    but it could also be appropriate for anything that you want to pursue further, 
    for example something that you want to try out for a longer time period. 
    This is a bit like adding something to a Favorites list - it lets you separate out the things that you're most interested in.
    </p>
    <p>If you do add some Picks, we'll ask you some questions about them in the Next Steps section, to help you move forward with next steps.</p
  </div>
`;


const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  prospects: PropTypes.arrayOf(PropTypes.object),
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  prospects: []
};

//content sub-component with unique content for this page
function ReviewTabChoosePicksContent(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const prospects = props.prospects;
  const picks = props.picks;
  const onRemoveProspect = props.onRemoveProspect;
  const onAddPick = props.onAddPick;
  const onRemovePick = props.onRemovePick;
  const onAddProspect = props.onAddProspect;

  const td_path = TABS.TEST;

  const haveProspects = (prospects.length > 0);
  const havePicks = (picks.length > 0);
  //just in case we get to this view without any prospects or picks:
  if(!haveProspects && !havePicks){
    return(
      <>
      <p><strong>Note:</strong> There are no Prospects or Picks shown here, possibly because you haven't completed any Test-drives yet.</p>
      <ul>
        <li>If that's the case, <Link to={td_path}>start a Test-drive</Link>, and keep stepping through the process until you end back here.</li>
      </ul>
      </>
    );
  }
  //the typical case where we do have Prospects or Picks:
  return(
    <>
    <p><strong>Prospects</strong> are access method setups that you’ve tried and are still considering.  
    As soon as you start a Test-drive with an access method, that access method automatically becomes a Prospect.</p>
    <p><strong>Your Picks</strong> are access method setups that you choose from Prospects, as  
      those that seem to have the most promise out of ones you’ve tried so far.  
      Picks are access methods that you plan to pursue further, either for continued trials or for actual delivery to the client. 
    </p>
    { haveProspects
      ?
      <>
      <Row>
        <Col>
          <p className="aa-list-heading"><strong>For each Prospect,</strong> do one of the following:</p>
          <ul>
            <li>To mark it as a promising option to pursue further, select Add to Picks</li>
            {/* <li>To remove it from further consideration, select Remove. Don't worry -- any data associated with a removed Prospect will still be available for you to review in {firstName}'s profile, if you wish.</li> */}
            <li>To keep it in the Prospects list, do nothing</li>
          </ul>
        </Col>
      </Row>
      <Row className="aa-review-row border-top">
        <Col>
          <h5><strong>Your Prospects</strong></h5>
          <TestDriveInfoTable
            testDrives={prospects}
            showDetailsCol={true}
            showCompareCol={false}
            showAddRemoveCol={true}
            showRemoveButton={false}
            onRemoveFromThisList={onRemoveProspect}
            onAddToOtherList={onAddPick}
          />
        </Col>
      </Row>
      </>
      : 
      <>
      <h5><strong>Your Prospects</strong></h5>
      <p>There are no Prospects right now. <span className="aa-more-info">(You'll get a new Prospect when you run your next Test-drive.)</span></p>
      </>
    }
    <Row className="aa-review-row border-top">
      <Col>
      <h5><strong>Your Picks</strong></h5>
      {havePicks
        ? <>
          <TestDriveInfoTable
            testDrives={picks}
            showDetailsCol={true}
            showCompareCol={false}
            showAddRemoveCol={true}
            showAddButton={false}
            onRemoveFromThisList={onRemovePick}
            onAddToOtherList={onAddProspect}
          />
          </>
        : <p>There are no Picks to display.</p>
      }
      </Col>
    </Row>
    </>
  );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function ReviewTabChoosePicks(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const onContinue = props.onContinue;
  const prospects = props.prospects;
  const picks = props.picks;
  const onRemoveProspect = props.onRemoveProspect;
  const onAddPick = props.onAddPick;
  const onRemovePick = props.onRemovePick;
  const onAddProspect = props.onAddProspect;
  const saveToSession = props.saveToSession;
  const pageData = {
    image: "./assets/images/target-icon.jpg",
    faIcon: "fa-thumbs-o-up",
    pageSymbolClassName: "aa-section-symbol review",
    title: `Make some decisions about your Prospects`,
    subTitle: ``,
    learnMore: {linkText: "Learn More about sorting prospects and picks",
                title: "Sorting Prospects and Picks",
                content: REVIEW_CHOICES_HELP
                },
    nextRoute: '', 
    sidebar: false,
    rightMargin: false
  }

  console.log("Rendering ReviewTabChoosePicks");

  useEffect(() => {
    return () => {
      saveToSession();
    }
  }, [saveToSession])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <ReviewTabChoosePicksContent 
          firstName = {firstName}
          pronouns={pronouns}
          prospects={prospects}
          picks={picks}
          onRemoveProspect={onRemoveProspect}
          onAddPick={onAddPick}
          onRemovePick={onRemovePick}
          onAddProspect={onAddProspect}
        />
      } 
      onContinue={onContinue} 
    />
    </>
  );
}

ReviewTabChoosePicks.propTypes = propTypes;
ReviewTabChoosePicks.defaultProps = defaultProps;

export default ReviewTabChoosePicks;